import { useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Eye, Mail, Copy, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { useEmailTemplates } from '@/lib/hooks/crm/useEmailTemplates';
import type { CrmEmailTemplate, CrmCreateEmailTemplateInput } from '@/lib/api';

interface TemplateForm {
  name: string;
  subject: string;
  bodyHtml: string;
  bodyText: string;
  variables: string;
}

const emptyForm: TemplateForm = {
  name: '',
  subject: '',
  bodyHtml: '',
  bodyText: '',
  variables: '',
};

function formToInput(form: TemplateForm): CrmCreateEmailTemplateInput {
  const variables = form.variables
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
  return {
    name: form.name,
    subject: form.subject,
    bodyHtml: form.bodyHtml || undefined,
    bodyText: form.bodyText || undefined,
    variables: variables.length > 0 ? variables : undefined,
  };
}

function templateToForm(template: CrmEmailTemplate): TemplateForm {
  return {
    name: template.name,
    subject: template.subject,
    bodyHtml: template.bodyHtml ?? '',
    bodyText: template.bodyText ?? '',
    variables: template.variables.join(', '),
  };
}

export function EmailTemplatesView() {
  const [search, setSearch] = useState('');
  const { templates, loading, createTemplate, updateTemplate, deleteTemplate, previewTemplate } =
    useEmailTemplates({ search: search || undefined });

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TemplateForm>({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const [previewVariables, setPreviewVariables] = useState<Record<string, string>>({});
  const [previewResult, setPreviewResult] = useState<{
    subject: string;
    bodyHtml: string | null;
    bodyText: string | null;
  } | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const [deleteId, setDeleteId] = useState<string | null>(null);

  function openCreate() {
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  }

  function openEdit(template: CrmEmailTemplate) {
    setEditingId(template.id);
    setForm(templateToForm(template));
    setShowForm(true);
  }

  function openPreview(template: CrmEmailTemplate) {
    setPreviewTemplateId(template.id);
    const vars: Record<string, string> = {};
    for (const v of template.variables) {
      vars[v] = '';
    }
    setPreviewVariables(vars);
    setPreviewResult(null);
    setShowPreview(true);
  }

  const handleSave = useCallback(async () => {
    if (!form.name.trim()) {
      toast({ title: 'Validation', description: 'Name is required', variant: 'destructive' });
      return;
    }
    if (!form.subject.trim()) {
      toast({ title: 'Validation', description: 'Subject is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const input = formToInput(form);
      if (editingId) {
        await updateTemplate(editingId, input);
        toast({ title: 'Template updated' });
      } else {
        await createTemplate(input);
        toast({ title: 'Template created' });
      }
      setShowForm(false);
    } catch {
      toast({ title: 'Error', description: 'Failed to save template', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }, [form, editingId, createTemplate, updateTemplate]);

  const handleDelete = useCallback(async () => {
    if (!deleteId) return;
    try {
      await deleteTemplate(deleteId);
      toast({ title: 'Template deleted' });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete template', variant: 'destructive' });
    } finally {
      setDeleteId(null);
    }
  }, [deleteId, deleteTemplate]);

  const handlePreview = useCallback(async () => {
    if (!previewTemplateId) return;
    setPreviewing(true);
    try {
      const result = await previewTemplate(previewTemplateId, previewVariables);
      setPreviewResult(result);
    } catch {
      toast({ title: 'Error', description: 'Failed to preview template', variant: 'destructive' });
    } finally {
      setPreviewing(false);
    }
  }, [previewTemplateId, previewVariables, previewTemplate]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates..."
            className="pl-9 pr-8"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 rounded-lg border bg-muted animate-pulse" />
            ))}
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Mail className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">No email templates</p>
            <p className="text-sm mt-1">Create templates to streamline your email outreach</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <Card key={template.id} className="hover:shadow-sm transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-sm font-medium truncate pr-2">
                      {template.name}
                    </CardTitle>
                    {template.variables.length > 0 && (
                      <Badge variant="secondary" className="text-xs shrink-0">
                        {template.variables.length} var{template.variables.length !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground truncate">{template.subject}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Created {new Date(template.createdAt).toLocaleDateString()}
                  </p>
                  <div className="flex items-center gap-1 mt-3 pt-3 border-t">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => openEdit(template)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => openPreview(template)}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        navigator.clipboard.writeText(template.bodyHtml ?? template.bodyText ?? '');
                        toast({ title: 'Copied to clipboard' });
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 ml-auto text-muted-foreground hover:text-destructive"
                      onClick={() => setDeleteId(template.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Template' : 'Create Template'}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Welcome Email"
              />
            </div>

            <div className="grid gap-2">
              <Label>Subject *</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Welcome to {{company_name}}"
              />
            </div>

            <div className="grid gap-2">
              <Label>Body HTML</Label>
              <Textarea
                value={form.bodyHtml}
                onChange={(e) => setForm({ ...form, bodyHtml: e.target.value })}
                rows={6}
                placeholder="<h1>Hello {{first_name}}</h1>..."
                className="font-mono text-sm"
              />
            </div>

            <div className="grid gap-2">
              <Label>Body Text</Label>
              <Textarea
                value={form.bodyText}
                onChange={(e) => setForm({ ...form, bodyText: e.target.value })}
                rows={3}
                placeholder="Plain text version..."
              />
            </div>

            <div className="grid gap-2">
              <Label>Variables (comma-separated)</Label>
              <Input
                value={form.variables}
                onChange={(e) => setForm({ ...form, variables: e.target.value })}
                placeholder="first_name, company_name, unsubscribe_url"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Preview Template</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {Object.keys(previewVariables).length > 0 && (
              <div className="grid gap-3">
                <Label className="text-sm font-medium">Variables</Label>
                {Object.keys(previewVariables).map((key) => (
                  <div key={key} className="grid grid-cols-3 gap-2 items-center">
                    <Label className="text-sm text-muted-foreground">{key}</Label>
                    <Input
                      className="col-span-2"
                      value={previewVariables[key]}
                      onChange={(e) =>
                        setPreviewVariables({ ...previewVariables, [key]: e.target.value })
                      }
                      placeholder={`Value for ${key}`}
                    />
                  </div>
                ))}
              </div>
            )}

            <Button onClick={handlePreview} disabled={previewing} variant="secondary">
              {previewing ? 'Rendering...' : 'Render Preview'}
            </Button>

            {previewResult && (
              <div className="space-y-3 border-t pt-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Subject</Label>
                  <p className="text-sm font-medium">{previewResult.subject}</p>
                </div>
                {previewResult.bodyHtml && (
                  <div>
                    <Label className="text-xs text-muted-foreground">HTML Preview</Label>
                    <div
                      className="mt-1 border rounded-md p-3 text-sm prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: previewResult.bodyHtml }}
                    />
                  </div>
                )}
                {previewResult.bodyText && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Text Preview</Label>
                    <pre className="mt-1 border rounded-md p-3 text-sm whitespace-pre-wrap bg-muted">
                      {previewResult.bodyText}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this template? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
